import { useApiQuery } from "@app/hooks/query";
import { CharacterType, DefaultAssetsType, ProductType, UserProduct } from "./models/user";
import { ApiOptions } from "./models/api";

export type DefaultProductsType = {
  characters: UserProduct[];
  sign: UserProduct;
  stage: UserProduct;
  emote: UserProduct;
  avatar: UserProduct
};

type HTTPHeaders = { [key: string]: string };

export const DEFAULT_PRODUCT_QUERY_KEY = ["default", "product"];
export const USER_PRODUCTS_STALE_TIME = 1 * 60 * 1000; // 1 minutes in ms

export async function getDefaultProducts(
  options: ApiOptions,
): Promise<DefaultProductsType> {
  // 1. Get the API base URL from the options
  const apiBaseUrl = options.config.baseUrl;

  const headerParameters: HTTPHeaders = {
    "Authorization": `Bearer ${process.env.STRAPI_API_TOKEN}`,
  };

  headerParameters['Content-Type'] = 'application/json';

  let defaultProducts: any;
  try {
    const response = await fetch(`${apiBaseUrl}/products/default`, {
        method: 'GET',
        headers: headerParameters,
        credentials: "include",
    });
    if (!response.ok) {
        console.log("Questions FourChoices not found");
    }

    defaultProducts = await response.json();
  } catch (error) {
      console.log("##### error :", error);
  }
  

  const characters = defaultProducts.data
    ?.filter(
      (product: { type: ProductType, default: boolean }) =>
        product.type === ProductType.Skin && product.default,
    )
    ?.map((defaultProduct: { character: string; id: string; }) => {
      const characterProducts = defaultProducts.data?.filter(
        (product: { type: ProductType, character: any }) =>
          product.type === ProductType.Skin &&
          product.character === defaultProduct.character,
      );
      if (characterProducts) {
        const userProduct: UserProduct = {
          id: defaultProduct.character + "-" + defaultProduct.id,
          productIdSelected: defaultProduct.id,
          products: characterProducts.map((product: { id: any, label: string, assets: DefaultAssetsType[], type: ProductType, character: CharacterType }) => {
            return {
              id: product.id,
              label: product.label as string,
              assets: product.assets as DefaultAssetsType[],
              type: product.type as ProductType,
              character: product.character as CharacterType,
            };
          }),
        };
        return userProduct;
      }
    }) as UserProduct[];

  const sign = defaultProducts.data
    ?.filter(
      (product: { type: ProductType, default: boolean }) =>
        product.type === ProductType.Sign && product.default,
    )
    ?.map((defaultProduct: { label: string, id: string }) => {
      const signProducts = defaultProducts.data?.filter(
        (product: { type: ProductType }) => product.type === ProductType.Sign,
      );
      if (signProducts) {
        const userProduct: UserProduct = {
          id: defaultProduct.label + "-" + defaultProduct.id,
          productIdSelected: defaultProduct.id,
          products: signProducts.map((product: { id: any, label: string, assets: DefaultAssetsType[], type: ProductType }) => {
            return {
              id: product.id,
              label: product.label as string,
              assets: product.assets as DefaultAssetsType[],
              type: product.type as ProductType,
            };
          }),
        };
        return userProduct;
      }
    })[0] as UserProduct;

  const stage = defaultProducts.data
    ?.filter(
      (product: { type: ProductType, default: boolean }) =>
        product.type === ProductType.Stage && product.default,
    )
    ?.map((defaultProduct: { label: string, id: string }) => {
      const stageProducts = defaultProducts.data?.filter(
        (product: { type: ProductType }) => product.type === ProductType.Stage,
      );
      if (stageProducts) {
        const userProduct: UserProduct = {
          id: defaultProduct.label + "-" + defaultProduct.id,
          productIdSelected: defaultProduct.id,
          products: stageProducts.map((product: { id: any, label: string, assets: DefaultAssetsType[], type: ProductType }) => {
            return {
              id: product.id,
              label: product.label as string,
              assets: product.assets as DefaultAssetsType[],
              type: product.type as ProductType,
            };
          }),
        };
        return userProduct;
      }
     
    })[0] as UserProduct;

  const emote = defaultProducts.data
    ?.filter(
      (product: { type: ProductType, default: boolean }) =>
        product.type === ProductType.Emote && product.default,
    )
    ?.map((defaultProduct: { label: string, id: string }) => {
      const emoteProducts = defaultProducts.data?.filter(
        (product: { type: ProductType }) => product.type === ProductType.Emote,
      );
      if (emoteProducts) {
        const userProduct: UserProduct = {
          id: defaultProduct.label + "-" + defaultProduct.id,
          productIdSelected: defaultProduct.id,
          products: emoteProducts.map((product: { id: any, label: string, assets: DefaultAssetsType[], type: ProductType }) => {
            return {
              id: product.id,
              label: product.label as string,
              assets: product.assets as DefaultAssetsType[],
              type: product.type as ProductType,
            };
          }),
        };
        return userProduct;
      }
    })[0] as UserProduct;

      const avatar = defaultProducts.data
    ?.filter(
      (product: { type: ProductType, default: boolean }) =>
        product.type === ProductType.Avatar && product.default,
    )
    ?.map((defaultProduct: { label: string, id: string }) => {
      const avatarProducts = defaultProducts.data?.filter(
        (product: { type: ProductType }) => product.type === ProductType.Avatar,
      );
      if (avatarProducts) {
        const userProduct: UserProduct = {
          id: defaultProduct.label + "-" + defaultProduct.id,
          productIdSelected: defaultProduct.id,
          products: avatarProducts.map((product: { id: any, label: string, assets: DefaultAssetsType[], type: ProductType }) => {
            return {
              id: product.id,
              label: product.label as string,
              assets: product.assets as DefaultAssetsType[],
              type: product.type as ProductType,
            };
          }),
        };
        return userProduct;
      }
    })[0] as UserProduct;

  return {
    characters,
    stage,
    sign,
    emote,
    avatar,
  };
}

// Query default products
export function useDefaultProducts() {
  const queryOptions = {
    queryKey: DEFAULT_PRODUCT_QUERY_KEY,
    staleTime: USER_PRODUCTS_STALE_TIME,
  };

  return useApiQuery<DefaultProductsType>(
    (context) => getDefaultProducts(context.apiOptions),
    queryOptions,
  );
}